// useLiveMetrics.js
// Drives the "live" numbers on the advertise pitch: a presence heartbeat for
// "online now", plus the hourly-cached Amplitude aggregates from /api/metrics.
//
// The heartbeat POSTs a per-tab id every 30s while the tab is visible. Stats
// are polled once a minute — they only change hourly server-side, so this is
// just to pick up a fresh snapshot without a reload.
import { useEffect, useRef, useState } from 'react';

const HEARTBEAT_MS = 30000;
const STATS_POLL_MS = 60000;

function tabId() {
  try {
    let id = sessionStorage.getItem('topingo_hb_id');
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('topingo_hb_id', id);
    }
    return id;
  } catch (e) {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function useLiveMetrics({ enabled = true } = {}) {
  const [metrics, setMetrics] = useState({
    online: null,
    visitors: null,
    newVisitors: null,
    plays: null,
    avgSessionMinutes: null,
    totalPlayHours: null,
    statsUpdatedAt: null,
    ready: false,
  });
  const idRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!idRef.current) idRef.current = tabId();
    let stopped = false;
    let heartbeatTimer = null;
    let statsTimer = null;

    const ping = () => {
      if (document.hidden) return;
      fetch('/api/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ v: idRef.current }),
        keepalive: true,
      }).catch(() => {});
    };

    const pullStats = () => {
      fetch('/api/metrics')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (stopped || !data || typeof data !== 'object' || data.error) return;
          setMetrics({
            online: typeof data.online === 'number' ? data.online : null,
            visitors: data.visitors ?? null,
            newVisitors: data.newVisitors ?? null,
            plays: data.plays ?? null,
            avgSessionMinutes: data.avgSessionMinutes ?? null,
            totalPlayHours: data.totalPlayHours ?? null,
            statsUpdatedAt: data.statsUpdatedAt ?? null,
            ready: true,
          });
        })
        .catch(() => {});
    };

    const onVisibility = () => {
      if (!document.hidden) {
        ping();
        pullStats();
      }
    };

    ping();
    pullStats();
    heartbeatTimer = setInterval(ping, HEARTBEAT_MS);
    statsTimer = setInterval(pullStats, STATS_POLL_MS);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      clearInterval(heartbeatTimer);
      clearInterval(statsTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);

  return metrics;
}
